import type { PostHog } from "posthog-node";
import {
  POST_HOG_CAPTURE_KEY,
  POST_HOG_HOST,
  ZUPLO_USER_ID_ARGV_KEY,
} from "../constants.js";
import { machineId } from "../machine-id/lib.js";
import { getPackageManagerUsageProperties } from "./package-manager.js";

interface IdentifyMessageV1 {
  distinctId: string;
  // biome-ignore lint/suspicious/noExplicitAny: Migrated from ESLint
  properties?: Record<string | number, any>;
  disableGeoip?: boolean;
}

interface EventMessageV1 extends IdentifyMessageV1 {
  event: string;
  groups?: Record<string, string | number>;
  sendFeatureFlags?: boolean;
  timestamp?: Date;
}

interface ZuploEventMessage extends Omit<EventMessageV1, "distinctId"> {
  // biome-ignore lint/suspicious/noExplicitAny: Migrated from ESLint
  argv: any;
}

interface UserContext {
  distinctId?: string;
  account?: string;
  project?: string;
}

// Upper bound on how long we wait for analytics/error events to reach PostHog
// before the CLI exits. A network stall must never prevent the process from
// exiting (this preserves the bounded-flush behavior the old Sentry.close had).
const ANALYTICS_FLUSH_TIMEOUT_MS = 2000;

let _postHog: PostHog | undefined;
let _initialized = false;

// Identity/context captured by the `identify` middleware so that error events
// can be attributed to the same person as product-analytics events, even
// though the failing call site has no access to argv.
let _userContext: UserContext = {};

// Resolves when `promise` settles or `ms` elapses, whichever comes first, so a
// hung network request can never block the CLI from exiting. Never rejects:
// analytics is best-effort and must not change CLI behavior.
async function withTimeout(
  promise: Promise<unknown>,
  ms: number
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
    // Don't let the timer itself keep the event loop alive.
    timer.unref?.();
  });
  await Promise.race([promise.catch(() => undefined), timeout]);
  if (timer) {
    clearTimeout(timer);
  }
}

async function init(): Promise<PostHog | undefined> {
  // biome-ignore lint/style/noProcessEnv: Migrated from ESLint
  if (process.env.ZUPLO_DO_NOT_TRACK) {
    return undefined;
  }

  if (_initialized) {
    return _postHog;
  }
  _initialized = true;

  try {
    // Imported lazily: posthog-node requires Node 20+, but the CLI still runs
    // (with a warning) on Node 18. A load failure on an unsupported runtime
    // must degrade to "no analytics", never crash the CLI.
    const { PostHog } = await import("posthog-node");
    _postHog = new PostHog(POST_HOG_CAPTURE_KEY, {
      host: POST_HOG_HOST,
      flushAt: 1,
      flushInterval: 1,
    });
  } catch {
    // Best effort only. Analytics cannot break the CLI.
    _postHog = undefined;
  }

  return _postHog;
}

/**
 * Records the current user/identity so subsequent error events can be
 * attributed correctly. Called by the `identify` middleware. This replaces the
 * previous `Sentry.setUser`/`Sentry.setTag` calls.
 */
export function setUserContext(context: UserContext): void {
  _userContext = { ..._userContext, ...context };
}

function getDistinctId(): string {
  return _userContext.distinctId ?? machineId();
}

// A convenience wrapper so that callers do not have to inject the distincId for each call to capture
export async function captureEvent({
  argv,
  event,
  properties,
  groups,
  sendFeatureFlags,
  timestamp,
  disableGeoip,
}: ZuploEventMessage): Promise<void> {
  const posthog = await init();

  if (posthog) {
    properties = {
      ...getPackageManagerUsageProperties(),
      ...(properties ?? {}),
    };
    properties.$set_once = properties.$set_once ?? {};

    // Set information about the user
    if (
      argv[ZUPLO_USER_ID_ARGV_KEY] &&
      typeof argv[ZUPLO_USER_ID_ARGV_KEY] === "string"
    ) {
      properties.$set_once["user-id"] = argv[ZUPLO_USER_ID_ARGV_KEY];
    }
    if (argv.account && typeof argv.account === "string") {
      properties.$set_once["account"] = argv["account"];
    }
    if (argv.project && typeof argv.project === "string") {
      properties.$set_once["project"] = argv["project"];
    }

    // Nice to have: set information if this is in CI
    // biome-ignore lint/style/noProcessEnv: Migrated from ESLint
    if (process.env.CI) {
      // biome-ignore lint/style/noProcessEnv: Migrated from ESLint
      properties["ci"] = process.env.CI;
    }

    posthog.capture({
      distinctId: (argv[ZUPLO_USER_ID_ARGV_KEY] as string) || machineId(),
      event,
      properties,
      groups,
      sendFeatureFlags,
      timestamp,
      disableGeoip,
    });
  }
}

/**
 * Captures an exception in PostHog error tracking. Replaces the previous
 * `Sentry.captureException`/`Sentry.captureMessage` usage.
 *
 * Uses the "immediate" variant so the exception is sent before the CLI exits,
 * rather than relying on a later flush, but bounds the wait so a stalled
 * request can't hang the process. Analytics must never change CLI behavior, so
 * any failure here is swallowed.
 */
export async function captureError(
  error: unknown,
  // biome-ignore lint/suspicious/noExplicitAny: Arbitrary error metadata
  properties?: Record<string | number, any>
): Promise<void> {
  const posthog = await init();
  if (!posthog) {
    return;
  }

  try {
    // biome-ignore lint/suspicious/noExplicitAny: Merged error metadata
    const contextProperties: Record<string, any> = {};
    if (_userContext.account) {
      contextProperties["account"] = _userContext.account;
    }
    if (_userContext.project) {
      contextProperties["project"] = _userContext.project;
    }
    // biome-ignore lint/style/noProcessEnv: Migrated from ESLint
    if (process.env.CI) {
      // biome-ignore lint/style/noProcessEnv: Migrated from ESLint
      contextProperties["ci"] = process.env.CI;
    }

    await withTimeout(
      posthog.captureExceptionImmediate(error, getDistinctId(), {
        ...contextProperties,
        ...(properties ?? {}),
      }),
      ANALYTICS_FLUSH_TIMEOUT_MS
    );
  } catch {
    // Analytics must never change CLI behavior.
  }
}

export async function shutdownAnalytics(): Promise<void> {
  if (!_postHog) {
    return;
  }
  // Bound the flush so a network stall can't keep the CLI from exiting.
  await withTimeout(_postHog.shutdown(), ANALYTICS_FLUSH_TIMEOUT_MS);
}
